// pages/MaintForecast.js — Fleet page "Maintenance forecast" overview.
// Data: GET /api/fleet/maintenance-forecast (model in api/maint_forecast.py).
// Timeline of the next N days per aircraft (projected due date + busy/quiet range, calendar limits,
// planned maintenance holds) and a "coming due" table whose Plan button deep-links into /schedule.
import React, { useEffect, useMemo, useState } from 'react';

const DAY = 86400000;
const C = {
  overdue: '#dc2626', grounding: '#ea580c', advisory: '#ca8a04', range: '#fed7aa', rangeAdv: '#fef08a',
  planned: '#7c3aed', today: '#2563eb', grid: '#eef1f6', muted: '#64748b',
};

const parse = s => (s ? new Date(s + 'T12:00:00') : null);
const fmtD = s => (s ? parse(s).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—');
const fmtDY = s => (s ? parse(s).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const daysFrom = s => (s ? Math.floor((parse(s) - new Date(new Date().toDateString())) / DAY) : null);

function leftNow(i) {
  const p = [];
  if (i.remaining_hours != null) p.push(i.remaining_hours < 0 ? `${Math.abs(i.remaining_hours).toFixed(1)} h over` : `${Number(i.remaining_hours).toFixed(1)} ${i.basis} h`);
  if (i.remaining_days != null) p.push(i.remaining_days < 0 ? `${-i.remaining_days} d over` : `${i.remaining_days} d`);
  return p.join(' · ') || '—';
}

function planDate(i) {
  // aim to be in the shop a few days before the earliest likely date
  const target = i.earliest_date || i.expected_date;
  if (!target) return null;
  const d = new Date(Math.max(parse(target) - 3 * DAY, Date.now()));
  return d.toISOString().slice(0, 10);
}

function Legend() {
  const sw = (bg, extra = {}) => <span style={{ display: 'inline-block', width: 14, height: 8, borderRadius: 4, background: bg, marginRight: 4, verticalAlign: 'middle', ...extra }} />;
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: C.muted, marginTop: 6 }}>
      <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 9, background: C.grounding, marginRight: 4, verticalAlign: 'middle' }} />projected due (grounding)</span>
      <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 9, background: C.advisory, marginRight: 4, verticalAlign: 'middle' }} />projected due (advisory)</span>
      <span>{sw(C.range)}busy-to-quiet range</span>
      <span><span style={{ display: 'inline-block', width: 8, height: 8, background: C.grounding, transform: 'rotate(45deg)', marginRight: 5, verticalAlign: 'middle' }} />calendar limit (fixed)</span>
      <span>{sw(C.planned, { opacity: 0.8 })}maintenance hold on schedule</span>
    </div>
  );
}

function Timeline({ aircraft, horizon, onPick }) {
  const t0 = new Date(new Date().toDateString()).getTime();
  const t1 = t0 + horizon * DAY;
  const pct = t => `${Math.min(Math.max((t - t0) / (t1 - t0), 0), 1) * 100}%`;
  const months = [];
  const m = new Date(t0); m.setDate(1); m.setMonth(m.getMonth() + 1);
  while (m.getTime() < t1) { months.push(new Date(m)); m.setMonth(m.getMonth() + 1); }

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 640 }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #dfe4ec', height: 20 }}>
          <div style={{ width: 190, flexShrink: 0 }} />
          <div style={{ flex: 1, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0, fontSize: 10, color: C.today, fontWeight: 700 }}>Today</span>
            {months.map(d => <span key={d} style={{ position: 'absolute', left: pct(d.getTime()), fontSize: 10, color: C.muted, paddingLeft: 3, borderLeft: '1px solid #dfe4ec', height: 20 }}>{(d.getTime() - t0) / (t1 - t0) > 0.06 ? d.toLocaleDateString([], { month: 'short' }) : ''}</span>)}
          </div>
        </div>
        {aircraft.map(a => {
          const u = a.utilization; const items = a.items || [];
          const dated = items.filter(i => i.expected_date || i.earliest_date);
          const missing = items.filter(i => i.baseline_missing && !i.expected_date).length;
          const tach = a.meters?.tach;
          return (
            <div key={a.fleet_id} style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', minHeight: 54 }}>
              <div style={{ width: 190, flexShrink: 0, padding: '7px 8px 7px 0', fontSize: 12 }}>
                <div style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13 }}>{a.tail_number} <span style={{ fontFamily: 'inherit', fontWeight: 400, color: C.muted, fontSize: 11 }}>{a.ac_type}</span></div>
                <div style={{ color: C.muted, fontSize: 11 }}>
                  {u.confidence === 'none' ? 'No recent flights tracked' : <>Flies ~{u.hours_per_week} h/wk{u.source === 'dispatch' ? ' (from check-ins)' : ''}{u.confidence === 'low' ? ' (little data)' : ''}</>}
                </div>
                {tach?.estimated != null && <div style={{ color: C.muted, fontSize: 11 }} title={`Recorded ${tach.recorded} at last check-in; +${a.flown_since_recorded_h} h airborne tracked since`}>
                  Tach ≈ {Number(tach.estimated).toFixed(1)}{a.flown_since_recorded_h > 0 ? ` (+${(tach.estimated - tach.recorded).toFixed(1)} est.)` : ''}</div>}
              </div>
              <div style={{ flex: 1, position: 'relative' }}>
                {months.map(d => <div key={d} style={{ position: 'absolute', left: pct(d.getTime()), top: 0, bottom: 0, borderLeft: `1px solid ${C.grid}` }} />)}
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, borderLeft: `2px solid ${C.today}` }} />
                {(a.maintenance_holds || []).map(h => {
                  const s = new Date(h.start_at).getTime(), e = Math.max(new Date(h.end_at).getTime(), s + DAY * 0.6);
                  if (s > t1) return null;
                  return <div key={h.id} title={`On schedule: ${h.title || 'Maintenance hold'} ${new Date(h.start_at).toLocaleDateString()}`}
                    style={{ position: 'absolute', left: pct(s), width: `calc(${pct(e)} - ${pct(s)} + 3px)`, top: 6, bottom: 6, background: C.planned, opacity: 0.75, borderRadius: 3 }} />;
                })}
                {items.length === 0 && <div style={{ position: 'absolute', left: 10, top: 18, fontSize: 11, color: C.muted }}>No maintenance items — use Set up → Maintenance on the card below</div>}
                {items.length > 0 && dated.length === 0 && <div style={{ position: 'absolute', left: 10, top: 18, fontSize: 11, color: '#92400e' }}>{missing} item{missing === 1 ? '' : 's'} need a last-done date/tach to forecast</div>}
                {dated.map((i, k) => {
                  const lane = 8 + (k % 3) * 13;
                  const col = i.status === 'overdue' ? C.overdue : i.grounding ? C.grounding : C.advisory;
                  const e = parse(i.expected_date || i.earliest_date).getTime();
                  const lo = parse(i.earliest_date || i.expected_date).getTime();
                  const hi = i.latest_date ? parse(i.latest_date).getTime() : t1 + DAY;
                  if (lo > t1) return null;
                  const cal = i.driver === 'calendar';
                  return (
                    <div key={i.id} onClick={() => onPick(i, a)} style={{ cursor: 'pointer' }}
                      title={`${i.name}: ${cal ? 'due' : 'projected'} ${fmtDY(i.expected_date)}${!cal ? ` (range ${fmtD(i.earliest_date)}–${i.latest_date ? fmtD(i.latest_date) : 'later'})` : ''} · ${leftNow(i)} left`}>
                      {!cal && <div style={{ position: 'absolute', left: pct(lo), width: `calc(${pct(hi)} - ${pct(lo)})`, top: lane + 2, height: 7, borderRadius: 4, background: i.grounding ? C.range : C.rangeAdv }} />}
                      {e <= t1 && (cal
                        ? <div style={{ position: 'absolute', left: `calc(${pct(e)} - 5px)`, top: lane + 1, width: 9, height: 9, background: col, transform: 'rotate(45deg)' }} />
                        : <div style={{ position: 'absolute', left: `calc(${pct(e)} - 5px)`, top: lane, width: 11, height: 11, borderRadius: 11, background: col, border: '2px solid #fff', boxShadow: '0 0 0 1px ' + col }} />)}
                      {e <= t1 && <span style={{ position: 'absolute', left: `calc(${pct(e)} + 9px)`, top: lane - 1, fontSize: 10, whiteSpace: 'nowrap', color: '#334155', background: 'rgba(255,255,255,0.85)', padding: '0 2px', borderRadius: 3 }}>
                        {i.name.replace(/ inspection$/i, '')}{i.planned ? ' ✓' : ''}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MaintForecastPanel({ apiFetch, token, navigate, role }) {
  const canBook = role !== 'maintenance';   // maintenance logins have no Schedule access
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [horizon, setHorizon] = useState(90);
  const [open, setOpen] = useState(true);
  const [showHow, setShowHow] = useState(false);
  const [hl, setHl] = useState(null);

  useEffect(() => {
    apiFetch('/api/fleet/maintenance-forecast', token).then(setData).catch(e => setErr(e.message));
  }, [apiFetch, token]);

  const rows = useMemo(() => {
    if (!data) return [];
    const r = [];
    (data.aircraft || []).forEach(a => (a.items || []).forEach(i => r.push({ a, i })));
    const key = x => x.i.status === 'overdue' ? -1e9 : (x.i.expected_date ? parse(x.i.expected_date).getTime() : x.i.baseline_missing ? 9e15 : 8e15);
    return r.sort((x, y) => key(x) - key(y));
  }, [data]);

  if (err) return <div className="card" style={{ color: '#b91c1c', fontSize: 13 }}>Maintenance forecast unavailable: {err}</div>;
  if (!data) return <div className="card" style={{ color: C.muted, fontSize: 13 }}>Loading maintenance forecast…</div>;

  const aircraft = data.aircraft || [];
  const soon = rows.filter(x => x.i.status === 'overdue' || (x.i.expected_date && daysFrom(x.i.earliest_date || x.i.expected_date) <= 30));
  const noSchedule = aircraft.filter(a => !(a.items || []).length).length;
  const plan = (x) => {
    const d = planDate(x.i);
    const q = new URLSearchParams({ fleet: x.a.fleet_id, kind: 'maintenance', title: x.i.name });
    if (d) q.set('date', d);
    navigate(`/schedule?${q.toString()}`);
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0 }}>Maintenance forecast</h3>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            {soon.length ? <b style={{ color: '#9a3412' }}>{soon.length} item{soon.length === 1 ? '' : 's'} due or likely due within 30 days · </b> : null}
            Projected from how much each aircraft has actually flown over the last 6 months (ADS-B tracks, or the school's own check-ins when those show more).
            {noSchedule ? ` ${noSchedule} aircraft have no maintenance items yet.` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {[60, 90, 180, 365].map(h => (
            <button key={h} className={h === horizon ? 'btn-primary' : 'btn-ghost'} style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setHorizon(h)}>{h === 365 ? '1 yr' : `${h} d`}</button>
          ))}
          <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'}</button>
        </div>
      </div>

      {open && <>
        <div style={{ marginTop: 12 }}>
          <Timeline aircraft={aircraft} horizon={horizon} onPick={(i) => { setHl(i.id); const el = document.getElementById(`mxrow-${i.id}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} />
          <Legend />
        </div>

        {rows.length > 0 && (
          <div style={{ overflowX: 'auto', marginTop: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 720 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  <th style={{ padding: '6px 6px' }}>Aircraft</th><th>Item</th><th>Left now</th><th>Projected due</th><th>Likely window</th><th>Plan</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(x => {
                  const { a, i } = x;
                  const over = i.status === 'overdue';
                  const within = i.expected_date && daysFrom(i.earliest_date || i.expected_date) <= 30;
                  const bg = i.id === hl ? '#eff6ff' : over ? '#fef2f2' : within ? '#fffbeb' : 'transparent';
                  return (
                    <tr key={i.id} id={`mxrow-${i.id}`} style={{ borderTop: '1px solid #f1f5f9', background: bg }}>
                      <td style={{ padding: '6px 6px', fontFamily: 'monospace', fontWeight: 700 }}>{a.tail_number}</td>
                      <td>{i.name}{i.grounding ? '' : <span style={{ color: C.muted }}> (advisory)</span>}{i.far_ref ? <span style={{ color: C.muted, fontSize: 11 }}> · {i.far_ref}</span> : null}</td>
                      <td className="mono">{i.baseline_missing && i.remaining_hours == null && i.remaining_days == null ? <span style={{ color: '#92400e' }}>needs last-done</span> : leftNow(i)}</td>
                      <td>
                        {over ? <b style={{ color: C.overdue }}>OVERDUE</b>
                          : i.expected_date ? <><b>{fmtDY(i.expected_date)}</b> <span style={{ color: C.muted }}>({daysFrom(i.expected_date)} d{i.driver === 'calendar' ? ', calendar' : ''})</span></>
                          : i.baseline_missing ? <span style={{ color: C.muted }}>—</span>
                          : <span style={{ color: C.muted }}>not flying enough to project</span>}
                      </td>
                      <td style={{ color: C.muted }}>{i.driver === 'hours' && i.earliest_date ? `${fmtD(i.earliest_date)} – ${i.latest_date ? fmtD(i.latest_date) : 'later'}` : i.driver === 'calendar' ? 'fixed date' : ''}</td>
                      <td>
                        {i.planned
                          ? <span style={{ color: C.planned, fontWeight: 600 }} title={i.planned.title}>✓ on schedule {new Date(i.planned.start_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                          : i.baseline_missing && !i.expected_date
                            ? <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => navigate(`/fleet/${a.fleet_id}?tab=maint`)}>Set baseline →</button>
                            : canBook ? <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => plan(x)}>Book maintenance →</button>
                              : <span style={{ color: C.muted, fontSize: 11 }}>not on schedule</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          <span onClick={() => setShowHow(!showHow)} style={{ fontSize: 12, color: C.today, cursor: 'pointer' }}>{showHow ? '▾' : '▸'} How these dates are estimated</span>
          {showHow && (
            <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.55, marginTop: 6, maxWidth: 820 }}>
              <p style={{ margin: '4px 0' }}><b>Flying pace.</b> Each aircraft's airborne time is measured from ADS-B tracks over the last 6 months — or from the Hobbs time recorded at check-in, whichever shows more flying in the last 90 days (marked "from check-ins") — weighted toward the most recent month. The "likely window" runs from a busy week (flying like its top quarter of weeks) to a quiet week (its bottom quarter).</p>
              <p style={{ margin: '4px 0' }}><b>Tach and Hobbs.</b> Airborne time is converted to meter time: Hobbs ≈ airborne + {data.model?.engine_overhead_h_per_flight ?? 0.25} h per flight for start, taxi and run-up; tach ≈ {data.model?.tach_per_hobbs ?? 0.85} × Hobbs. Once an aircraft has five or more clean dispatch check-ins, its own measured ratio is used instead. The last recorded reading is rolled forward by the flights tracked since, so "Tach ≈" is an estimate until the next check-in.</p>
              <p style={{ margin: '4px 0' }}><b>What's already booked.</b> If confirmed schedule bookings add up to more flying than the aircraft's usual pace, the forecast uses the bookings (counting about {Math.round((data.model?.block_to_air ?? 0.7) * 100)}% of each booked block as airborne).</p>
              <p style={{ margin: '4px 0' }}><b>Calendar items</b> (annual, transponder, ELT…) use the fixed FAR end-of-month date. An item that has both an hours and a calendar limit shows whichever comes first.</p>
              <p style={{ margin: '4px 0' }}><b>Scheduling warnings.</b> A booking warns when the aircraft is expected to reach the limit by the end of that booking, or could reach it on a busy pace. Putting a maintenance hold on the schedule — titled with the item's name, or just "Maintenance" — before the booking clears the warning.</p>
            </div>
          )}
        </div>
      </>}
    </div>
  );
}
